import type { PrecursorTier, RecipeDef, StageDef, SourcingDef } from "@reagent/sim";
import { freshCardInstances } from "@reagent/sim";
import { ALL_CARDS } from "../../cards";
import { pollutionCardsFor, reachablePollutionReasons } from "../../pollution";
import { setupOfferFor, validateStage } from "../../schema";
import type { StageSolution } from "../../solutions";
import { COLOUR_CRITICAL_TEMP, MELT_BUFFER, MELT_CRITICAL_TEMP } from "./machines";
import { ERA_1_SCALARS } from "./shared";

/**
 * Era 1's Glass & glaze pathway (spec 0037, `docs/design/06-eras-and-stages.md`
 * §4.3/§4.6): a core melt that takes four feeds at one batch ratio and skims the
 * glass off its gall, and a refined tier that colours the glass in a reactor
 * that runs hot. Every number marked PLACEHOLDER is a stage-author guess chosen
 * so the worked solution clears; none has been playtested.
 *
 * Two engine facts shape both layouts:
 *
 *  - A Budget packet carries `Sludge`, so it is never "awaiting a partner": a
 *    reactor ships a Budget feed onward the tick after it lands unless the rest
 *    of its batch has landed too. Four feeds therefore only melt if they arrive
 *    within a tick of each other, which is what the storage tanks are for.
 *    Storage adds two ticks of latency per tank (a hold and a pipe segment) and
 *    an arrival skew of two or more jams the melt with stale packets.
 *  - A reaction product is born at ambient and leaves in the same packet as
 *    whatever the recipe did not consume, so the skim, not the melt, is what
 *    strips the Budget `Sludge` off the glass.
 *
 * Both reactors and the skim are pre-placed and bound with `recipeIds` (spec 0031),
 * so the two tiers share a plant without seeing each other's inputs. The skim's
 * output edges carry authored roles: a bought pipe has none, and the glass would
 * never leave.
 */

// PLACEHOLDER (spec 0037): needs playtest. Batch ratio Sand 2 : Alkali 1 : Lime 1 : Fuel 2.
export const GL_MELT: RecipeDef = {
  id: "gl-melt",
  inputs: { Sand: 2, Alkali: 1, Lime: 1, Fuel: 2 },
  outputs: { Glass: 3, Brine: 1 },
  isExothermic: true,
  heat: 1,
  nodeKinds: ["reactor"],
};

/** The skim sends `Glass` down `main`; the glass gall (`Brine`) and the Budget `Sludge` are the waste fraction. */
export const GL_SKIM: RecipeDef = {
  id: "gl-skim",
  inputs: { Glass: 3, Brine: 1 },
  outputs: { Glass: 3, Brine: 1 },
  isExothermic: false,
  heat: 0,
  nodeKinds: ["separator"],
  primarySpecies: ["Glass"],
};

// PLACEHOLDER (spec 0037): needs playtest. Mass-conserving so a Budget feed's Sludge stays a small share of the packet (there is no skim in the refined tier).
export const GL_COLOUR: RecipeDef = {
  id: "gl-colour",
  inputs: { Glass: 8, Oxide: 4 },
  outputs: { ColouredGlass: 12 },
  isExothermic: true,
  heat: 1,
  nodeKinds: ["reactor"],
};

export const GL_RECIPES: readonly RecipeDef[] = [GL_MELT, GL_SKIM, GL_COLOUR];

/** Maps every reachable pollution reason (spec 0029) rather than hand-listing them, so a change to the board cannot leave one unmapped. */
function finish(bare: StageDef): StageDef {
  return validateStage(
    { ...bare, pollutionCards: pollutionCardsFor(...reachablePollutionReasons(bare, ALL_CARDS)) },
    ALL_CARDS,
  );
}

const card = (id: string) => {
  const found = ALL_CARDS.find((c) => c.id === id);
  if (!found) throw new Error(`gl: unknown card ${id}`);
  return found;
};

const tile = (x: number, y: number) => ({ x, y });

// PLACEHOLDER (spec 0037): needs playtest. Budget undercuts Premium by 5 credits; the market may double Budget.
const BUDGET_COST = 5;
const PREMIUM_COST = 10;

/** One Budget `Sludge` unit per packet: the market may double it, and both tiers still clear `ERA_1_SCALARS.minPurity`. */
function contract(): SourcingDef {
  return {
    budget: { impurities: { Sludge: 1 }, costCredits: BUDGET_COST },
    premium: { costCredits: PREMIUM_COST },
  };
}

// PLACEHOLDER (spec 0037): needs playtest. Each packet is two batches of its species (Sand 4 = 2 x 2, Fuel 4 = 2 x 2), so the melt fires at k = 2.
const SAND_PER_TICK = 4;
const ALKALI_PER_TICK = 2;
const LIME_PER_TICK = 2;
const FUEL_PER_TICK = 4;
// PLACEHOLDER (spec 0037): needs playtest. Two firings' worth of glass (6 a firing at k = 2).
const CORE_QUOTA = 12;
// PLACEHOLDER (spec 0037): needs playtest. Rig 40 (two storage tanks, four pipes) plus Budget contracts at 2x (40) fits with 10 spare.
const CORE_SETUP_BUDGET = 90;

/**
 * Glass & glaze core. Lesson: Ratio (four feeds). The sand and alkali belts are
 * pre-laid and run round the flue, landing two ticks after a direct Lime or Fuel
 * pipe would; the player buys a storage tank on each of those two lines to hold
 * them back into step, then plays a Surge Drum (room for the batch) and a Cooling
 * Jacket (the furnace, `03-hazards.md` §2.1) on the melt.
 */
export const GL_CORE: StageDef = finish({
  id: "era1-gl-core",
  name: "Glass & glaze: the melt",
  blurb: "Four feeds, one batch ratio: hold the fast lines back until the slow ones land, and let the skim take the gall.",
  cols: 9,
  rows: 5,
  nodes: [
    { id: "gl-sand", kind: "inputTank", pos: tile(0, 0), emits: { Sand: SAND_PER_TICK }, sourcing: contract() },
    { id: "gl-alkali", kind: "inputTank", pos: tile(0, 1), emits: { Alkali: ALKALI_PER_TICK }, sourcing: contract() },
    { id: "gl-lime", kind: "inputTank", pos: tile(0, 3), emits: { Lime: LIME_PER_TICK }, sourcing: contract() },
    { id: "gl-fuel", kind: "inputTank", pos: tile(0, 4), emits: { Fuel: FUEL_PER_TICK }, sourcing: contract() },
    { id: "gl-melt", kind: "reactor", pos: tile(4, 2), criticalTemp: MELT_CRITICAL_TEMP, bufferCapacity: MELT_BUFFER, recipeIds: ["gl-melt"] },
    { id: "gl-skim", kind: "separator", pos: tile(5, 2), recipeIds: ["gl-skim"] },
    { id: "gl-hopper", kind: "productHopper", pos: tile(8, 2), bufferCapacity: 99 },
    { id: "gl-wastebuyer", kind: "wasteBuyer", pos: tile(8, 3) },
  ],
  edges: [
    {
      id: "gl-sand-feed",
      fromNodeId: "gl-sand",
      toNodeId: "gl-melt",
      path: [tile(0, 0), tile(1, 0), tile(2, 0), tile(3, 0), tile(4, 0), tile(5, 0), tile(5, 1), tile(4, 1), tile(4, 2)],
    },
    {
      id: "gl-alkali-feed",
      fromNodeId: "gl-alkali",
      toNodeId: "gl-melt",
      path: [tile(0, 1), tile(1, 1), tile(1, 2), tile(2, 2), tile(2, 1), tile(3, 1), tile(4, 1), tile(4, 2)],
    },
    { id: "gl-melt-out", fromNodeId: "gl-melt", toNodeId: "gl-skim", path: [tile(4, 2), tile(5, 2)] },
    {
      id: "gl-glass-main",
      fromNodeId: "gl-skim",
      toNodeId: "gl-hopper",
      separationRole: "main",
      path: [tile(5, 2), tile(6, 2), tile(7, 2), tile(8, 2)],
    },
    {
      id: "gl-gall-waste",
      fromNodeId: "gl-skim",
      toNodeId: "gl-wastebuyer",
      separationRole: "waste",
      path: [tile(5, 2), tile(5, 3), tile(6, 3), tile(7, 3), tile(8, 3)],
    },
  ],
  recipes: [GL_MELT, GL_SKIM],
  objective: { targetComposition: { Glass: CORE_QUOTA }, minPurity: ERA_1_SCALARS.minPurity },
  energyBudgetKw: ERA_1_SCALARS.energyBudgetKw,
  handSize: ERA_1_SCALARS.handSize,
  ticksPerTurn: ERA_1_SCALARS.ticksPerTurn,
  maxTurns: ERA_1_SCALARS.maxTurns,
  liveBuild: ERA_1_SCALARS.liveBuild,
  corrosiveSpecies: [],
  startingDeck: freshCardInstances([
    "card-surge-drum",
    "card-cooling-jacket",
    "card-cooling-jacket-twin",
    "card-solvent-flush",
    "card-emergency-flush",
    "card-rush-order",
    "card-overtime",
  ]),
  setupBudgetCredits: CORE_SETUP_BUDGET,
  setupCatalogue: [
    setupOfferFor(card("card-gl-melt")),
    setupOfferFor(card("card-mixer")),
    setupOfferFor(card("card-mixer-heavy")),
    setupOfferFor(card("card-storage-tank")),
    setupOfferFor(card("card-pipe-glass")),
  ],
});

// PLACEHOLDER (spec 0037): needs playtest. Each packet is one batch (Glass 8 + Oxide 4), so the reactor fires at k = 1.
const GLASS_PER_TICK = 8;
const OXIDE_PER_TICK = 4;
// PLACEHOLDER (spec 0037): needs playtest. Four firings of 12: the hot reactor explodes on its fifth uncooled firing, and the first packet only reaches the hopper a tick after that.
const REFINED_QUOTA = 48;
// PLACEHOLDER (spec 0037): needs playtest. Two Budget contracts at 2x (20) and two spare pipes (10) with room to spare.
const REFINED_SETUP_BUDGET = 40;

/**
 * Glass & glaze refined. Lesson: Cooling. Colouring runs hot: fed a batch a tick
 * the reactor fires every tick, and with no jacket its heat reaches the critical
 * temperature on the fourth firing and it explodes on the fifth, a tick before the
 * first coloured glass arrives. The pre-laid lines need nothing; the Cooling
 * Jacket played in the Play phase is the whole answer.
 */
export const GL_REFINED: StageDef = finish({
  id: "era1-gl-refined",
  name: "Glass & glaze: the colour",
  blurb: "Stain the glass with copper oxide in a reactor that runs hot, and cool it before it goes.",
  cols: 9,
  rows: 5,
  nodes: [
    { id: "gl-r-glass", kind: "inputTank", pos: tile(0, 1), emits: { Glass: GLASS_PER_TICK }, sourcing: contract() },
    { id: "gl-r-oxide", kind: "inputTank", pos: tile(0, 3), emits: { Oxide: OXIDE_PER_TICK }, sourcing: contract() },
    { id: "gl-r-colour", kind: "reactor", pos: tile(4, 2), criticalTemp: COLOUR_CRITICAL_TEMP, recipeIds: ["gl-colour"] },
    { id: "gl-r-hopper", kind: "productHopper", pos: tile(8, 2), bufferCapacity: 99 },
  ],
  edges: [
    {
      id: "gl-r-glass-feed",
      fromNodeId: "gl-r-glass",
      toNodeId: "gl-r-colour",
      path: [tile(0, 1), tile(1, 1), tile(2, 1), tile(3, 1), tile(4, 1), tile(4, 2)],
    },
    {
      id: "gl-r-oxide-feed",
      fromNodeId: "gl-r-oxide",
      toNodeId: "gl-r-colour",
      path: [tile(0, 3), tile(1, 3), tile(2, 3), tile(3, 3), tile(4, 3), tile(4, 2)],
    },
    {
      id: "gl-r-product",
      fromNodeId: "gl-r-colour",
      toNodeId: "gl-r-hopper",
      path: [tile(4, 2), tile(5, 2), tile(6, 2), tile(7, 2), tile(8, 2)],
    },
  ],
  recipes: [GL_COLOUR],
  objective: { targetComposition: { ColouredGlass: REFINED_QUOTA }, minPurity: ERA_1_SCALARS.minPurity },
  energyBudgetKw: ERA_1_SCALARS.energyBudgetKw,
  handSize: ERA_1_SCALARS.handSize,
  ticksPerTurn: ERA_1_SCALARS.ticksPerTurn,
  maxTurns: ERA_1_SCALARS.maxTurns,
  liveBuild: ERA_1_SCALARS.liveBuild,
  corrosiveSpecies: [],
  startingDeck: freshCardInstances([
    "card-cooling-jacket",
    "card-cooling-jacket-twin",
    "card-flash-freeze",
    "card-solvent-flush",
    "card-rush-order",
    "card-overtime",
  ]),
  setupBudgetCredits: REFINED_SETUP_BUDGET,
  setupCatalogue: [
    setupOfferFor(card("card-gl-colour")),
    setupOfferFor(card("card-mixer")),
    setupOfferFor(card("card-mixer-heavy")),
    setupOfferFor(card("card-pipe-glass")),
  ],
});

const BUDGET_ON_EVERY_IMPORT: Readonly<Record<string, PrecursorTier>> = {
  "gl-sand": "budget",
  "gl-alkali": "budget",
  "gl-lime": "budget",
  "gl-fuel": "budget",
};

export const GL_CORE_SOLUTION: StageSolution = {
  stageId: GL_CORE.id,
  sourcing: BUDGET_ON_EVERY_IMPORT,
  setupBuild: [
    // Storage tanks mint n1 and n2; the pipes that name them come after.
    { cardId: "card-storage-tank", at: tile(2, 3) }, // n1: holds the Lime line back two ticks
    { cardId: "card-storage-tank", at: tile(2, 4) }, // n2: holds the Fuel line back two ticks
    { cardId: "card-pipe-glass", fromNodeId: "gl-lime", toNodeId: "n1" },
    { cardId: "card-pipe-glass", fromNodeId: "n1", toNodeId: "gl-melt" },
    { cardId: "card-pipe-glass", fromNodeId: "gl-fuel", toNodeId: "n2" },
    { cardId: "card-pipe-glass", fromNodeId: "n2", toNodeId: "gl-melt" },
  ],
  turns: [
    [
      { cardId: "card-surge-drum", at: tile(4, 2) },
      { cardId: "card-cooling-jacket", at: tile(4, 2) },
    ],
  ],
  notes:
    "The sand and alkali belts are laid round the flue and land two ticks late; a direct Lime or Fuel pipe would " +
    "land first, and a Budget feed that lands before its partners is shipped on to the skim (its Sludge means it is " +
    "never 'awaiting a partner'), so the reactor jams on stale packets. One storage tank on each of those two lines " +
    "adds exactly two ticks and puts all four feeds into step, and the melt fires at k = 2 every tick. The Surge Drum " +
    "gives the melt room for a batch plus the packet leaving it; the Cooling Jacket holds the furnace under " +
    "criticalTemp until the quota lands. Total 3 kW.",
};

export const GL_REFINED_SOLUTION: StageSolution = {
  stageId: GL_REFINED.id,
  sourcing: { "gl-r-glass": "budget", "gl-r-oxide": "budget" },
  turns: [[{ cardId: "card-cooling-jacket", at: tile(4, 2) }]],
  notes:
    "Both feeds land together and the reactor fires every tick. Colouring is exothermic and the reactor's " +
    "criticalTemp is 4, so uncooled it explodes on the fifth firing; one Cooling Jacket sheds the one heat each " +
    "firing makes. Total 1 kW.",
};
